import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

// A lightweight, in-memory "clipboard" for moving keywords between topics.
// Lives above the topic work view so a cut/copy in one topic survives
// navigating to another topic before pasting. Holds topic_keyword ids only;
// the actual move/copy API call happens at paste time in the consuming pane.

export type KeywordClipboardMode = 'cut' | 'copy';

export interface KeywordClipboard {
  ids: number[]; // topic_keyword ids
  mode: KeywordClipboardMode;
  sourceTopicId: number;
}

interface KeywordClipboardContextType {
  clipboard: KeywordClipboard | null;
  cut: (ids: number[], sourceTopicId: number) => void;
  copy: (ids: number[], sourceTopicId: number) => void;
  clear: () => void;
}

const KeywordClipboardContext = createContext<
  KeywordClipboardContextType | undefined
>(undefined);

export function KeywordClipboardProvider({ children }: { children: ReactNode }) {
  const [clipboard, setClipboard] = useState<KeywordClipboard | null>(null);

  const cut = useCallback((ids: number[], sourceTopicId: number) => {
    if (ids.length === 0) return;
    setClipboard({ ids: [...ids], mode: 'cut', sourceTopicId });
  }, []);

  const copy = useCallback((ids: number[], sourceTopicId: number) => {
    if (ids.length === 0) return;
    setClipboard({ ids: [...ids], mode: 'copy', sourceTopicId });
  }, []);

  const clear = useCallback(() => setClipboard(null), []);

  return (
    <KeywordClipboardContext.Provider value={{ clipboard, cut, copy, clear }}>
      {children}
    </KeywordClipboardContext.Provider>
  );
}

export function useKeywordClipboard() {
  const ctx = useContext(KeywordClipboardContext);
  if (ctx === undefined) {
    throw new Error(
      'useKeywordClipboard must be used within a KeywordClipboardProvider'
    );
  }
  return ctx;
}
